import React from 'react'

import * as c from '../../../common'

import * as cp from '../../../app/components'
import * as gs from '../../../app/gStyles'
//@ts-ignore
import gStyles from '../../global.module.css'

//@ts-ignore
import styles from './Dialog.module.css'

export type DialogProps = c.r<{
  children: React.ReactNode
  fullWidth?: boolean
  // Content fills the whole viewport edge to edge: no canvas frame, no
  // padding, no max width, black backdrop and a floating close button over
  // the content. Used for presentations (CCTV simulator on the iPad).
  fullScreen?: boolean
  // Caller renders its own close affordance positioned against its own
  // content (e.g. pinned to a variable-width image) instead of the generic
  // one below, which is placed against the full-screen backdrop and only
  // lines up with content that happens to fill it edge to edge.
  hideClose?: boolean
  noCanvas?: boolean
  onClose(): void
  opaque?: boolean
  open: boolean
  subtitle?: string
  title?: string
}>

// let scrollY = '0'

export default React.memo<DialogProps>(function Dialog({
  children,
  fullWidth,
  fullScreen,
  hideClose,
  noCanvas,
  onClose,
  opaque,
  open,
  subtitle,
  title,
}) {
  // React.useEffect(() => {
  //   if (open) {
  //     scrollY = document.body.style.top
  //     document.body.style.position = 'fixed'
  //     document.body.style.top = `-${scrollY}px`
  //   } else {
  //     document.body.style.position = ''
  //     document.body.style.top = ''
  //     window.scrollTo(0, parseInt(scrollY || '0') * -1)
  //   }
  // }, [open])
  const fullScreenRef = React.useRef<HTMLDivElement | null>(null)
  // Real fullscreen on top of filling the window, so on the iPad the Safari
  // address and tab bars go away too. Runs in a layout effect so it is still
  // inside the tap that opened the dialog (browsers only allow fullscreen from
  // a user gesture). Unsupported or refused: the dialog still fills the window.
  React.useLayoutEffect(() => {
    if (!fullScreen || !open) return
    const el = fullScreenRef.current as
      | (HTMLDivElement & { webkitRequestFullscreen?: () => void })
      | null
    try {
      if (el?.requestFullscreen) el.requestFullscreen().catch(() => {})
      else el?.webkitRequestFullscreen?.()
    } catch {
      // Ignored on purpose, see above.
    }
    return () => {
      const doc = document as Document & {
        webkitExitFullscreen?: () => void
        webkitFullscreenElement?: Element | null
      }
      try {
        if (doc.fullscreenElement) doc.exitFullscreen().catch(() => {})
        else if (doc.webkitFullscreenElement) doc.webkitExitFullscreen?.()
      } catch {
        // Ignored on purpose, see above.
      }
    }
  }, [fullScreen, open])

  const backdropClass = opaque ? styles['backdrop-opaque'] : styles['backdrop']

  if (fullScreen) {
    return (
      <div
        ref={fullScreenRef}
        className={
          open ? styles['backdrop-fullscreen'] : gStyles['display-none']
        }
      >
        {children}

        {!hideClose && (
          <div
            aria-label="Close"
            className={styles['close-container-fullscreen']}
            onClick={onClose}
            role="button"
          >
            <span className={styles['close-fullscreen']}>&times;</span>
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={open ? backdropClass : gStyles['display-none']}
      onClick={onClose}
    >
      <div
        className={
          noCanvas
            ? fullWidth
              ? styles['dialog-content']
              : styles['dialog-content-constrained']
            : fullWidth
            ? styles['dialog-content-canvas']
            : styles['dialog-content-canvas-constrained']
        }
        // Stop the backdrop's own onClose-on-click from firing for clicks
        // that land on the content itself - only a click that actually
        // reaches the backdrop (outside the content) should close it.
        onClick={(e) => e.stopPropagation()}
      >
        {!noCanvas && !!title && (
          <>
            <div className={styles['dialog-header']}>
              <div className={styles['dialog-header-text']}>
                {!!title && (
                  <cp.Text bold centerText size={24}>
                    {title}
                  </cp.Text>
                )}
                {!!subtitle && (
                  <cp.Text centerText size={14}>
                    {subtitle}
                  </cp.Text>
                )}
              </div>

              <div
                className={styles['close-container-canvas']}
                onClick={onClose}
              >
                <span className={styles['close']}>&times;</span>
              </div>
            </div>

            <div className={gStyles['pad-v-12']} />
          </>
        )}

        {children}

        {!!title && gs.rowGap28}
      </div>

      {(noCanvas || !title) && !hideClose && (
        <div className={styles['close-container']} onClick={onClose}>
          <span className={styles['close']}>&times;</span>
        </div>
      )}

      {gs.rowGap256}
    </div>
  )
})
